import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from '@app/shared/api/api.service';
import { ApiResponse } from '@app/shared/api/model/api.model';
import { ValueLabel } from '@app/shared-ui/common/model/value-label';

/**
 * Mirrors backend CategoryWithSubcategories DTO.
 *  - value/label: the parent category (e.g. "apartment")
 *  - subcategories: child categories (the `subCategory` value sent on
 *    CreatePropertyListingRequest is one of these subcategory values)
 */
export type PropertyListingCategory = ValueLabel & {
  subcategories: ValueLabel[];
};

/**
 * Backend GET /metadata response payload (inside ApiResponse<T>.data).
 * Backend shape (C#):
 *   {
 *     PropertyListingAmenities: enum items,
 *     PropertyListingCategories: List<CategoryWithSubcategories>
 *   }
 * ASP.NET serializes properties to camelCase by default.
 */
export interface MetadataResponse {
  propertyListingCategories: PropertyListingCategory[];
  propertyListingAmenities: ValueLabel[];
  detailedReportPrice: number;
  detailedReportEnabled: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class MetadataService {
  private apiService = inject(ApiService);

  private propertyListingCategoriesSubject = new BehaviorSubject<
    PropertyListingCategory[]
  >([]);
  readonly propertyListingCategories$: Observable<PropertyListingCategory[]> =
    this.propertyListingCategoriesSubject.asObservable();

  private propertyListingAmenitiesSubject = new BehaviorSubject<ValueLabel[]>(
    [],
  );
  readonly propertyListingAmenities$: Observable<ValueLabel[]> =
    this.propertyListingAmenitiesSubject.asObservable();

  private detailedReportPriceSubject = new BehaviorSubject<number>(0);
  readonly detailedReportPrice$: Observable<number> =
    this.detailedReportPriceSubject.asObservable();

  private detailedReportEnabledSubject = new BehaviorSubject<boolean>(false);
  readonly detailedReportEnabled$: Observable<boolean> =
    this.detailedReportEnabledSubject.asObservable();

  private loadingSubject = new BehaviorSubject<boolean>(false);
  readonly loading$: Observable<boolean> = this.loadingSubject.asObservable();

  /**
   * Loads metadata from the API. Designed to be called once at app
   * initialization (see provideAppInitializer in app.config). Errors are
   * swallowed so a failing metadata call never blocks the app from booting —
   * components fall back to empty dropdowns instead.
   */
  loadMetadata(): Observable<MetadataResponse> {
    this.loadingSubject.next(true);

    return this.apiService
      .get<ApiResponse<MetadataResponse>>({
        path: '/metadata',
      })
      .pipe(
        map((response) => {
          const metadata = response.data;
          this.propertyListingCategoriesSubject.next(
            metadata.propertyListingCategories ?? [],
          );
          this.propertyListingAmenitiesSubject.next(
            metadata.propertyListingAmenities ?? [],
          );
          this.detailedReportPriceSubject.next(
            metadata.detailedReportPrice ?? 0,
          );
          this.detailedReportEnabledSubject.next(
            metadata.detailedReportEnabled === true,
          );
          this.loadingSubject.next(false);
          return metadata;
        }),
        catchError(() => {
          this.loadingSubject.next(false);
          return of<MetadataResponse>({
            propertyListingCategories: [],
            propertyListingAmenities: [],
            detailedReportPrice: 0,
            detailedReportEnabled: false,
          });
        }),
      );
  }

  getPropertyListingCategories(): PropertyListingCategory[] {
    return this.propertyListingCategoriesSubject.value;
  }

  getPropertyListingAmenities(): ValueLabel[] {
    return this.propertyListingAmenitiesSubject.value;
  }

  getDetailedReportPrice(): number {
    return this.detailedReportPriceSubject.value;
  }

  isDetailedReportEnabled(): boolean {
    return this.detailedReportEnabledSubject.value;
  }

  /**
   * Look up the parent category by one of its subcategory values. Useful for
   * edit mode when the listing only stores `subCategory` and we need to
   * pre-select the parent dropdown.
   */
  findCategoryBySubcategoryValue(
    subcategoryValue: string,
  ): PropertyListingCategory | null {
    if (!subcategoryValue) return null;
    return (
      this.propertyListingCategoriesSubject.value.find((c) =>
        c.subcategories.some((s) => s.value === subcategoryValue),
      ) ?? null
    );
  }
}
